import React from "react";
import {
  Grid,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Dialog,
  Box,
  Stack,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import "react-quill/dist/quill.snow.css";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../globalStyles/fonts";
import { useDarkMode } from "../context/darkModeContext";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { formatDate, formatPoDate } from "../../utils/formatDate";
import POArrivalsView from "../po-arrivals-view";
import { hasArrivals, receivingTotals } from "../../utils/poArrivals";
import { warehouseLabel } from "../../utils/warehouseLabel";

/**
 * Money, in the PO's OWN currency.
 *
 * Deliberately not hardcoding "$": these figures come from the source PO, and
 * a provider that reports EUR must not be rendered as dollars. When the
 * provider sends no currency the number is shown bare rather than guessed at.
 */
const formatMoney = (value, currency) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const amount = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${amount} ${currency}` : amount;
};

/** Whether the PO carries any of the provider-optional commercial detail. The
 *  whole card is hidden otherwise — an empty "Costs" card on a portal-created
 *  PO would read as "this PO is worth nothing". */
const hasCommercialDetail = (asn) =>
  Boolean(
    asn?.costs?.total != null ||
      asn?.costs?.sub_total != null ||
      asn?.costs?.tax != null ||
      asn?.costs?.additional?.length ||
      asn?.shipping_details?.carrier ||
      asn?.shipping_details?.service ||
      asn?.shipping_details?.incoterms ||
      asn?.shipping_details?.notes ||
      asn?.tags?.length,
  );

// Reusable read-only field component
const ReadOnlyField = ({ label, value, colors }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: colors?.textTertiary || "var(--text-secondary)",
        fontWeight: 500,
        mb: 0.5,
        display: "block",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        backgroundColor: colors?.bgSecondary || "#F5F7FA",
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        minHeight: 38,
        display: "flex",
        alignItems: "center",
        border: `1px solid ${colors?.border || "#E8ECF0"}`,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: value
            ? colors?.textSecondary || "var(--text-tertiary)"
            : colors?.textTertiary || "var(--text-secondary)",
          fontWeight: 400,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

// Section header component
const SectionHeader = ({ icon: Icon, title, colors }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Box
      sx={{
        backgroundColor: colors?.accentBg || "#E8F5F5",
        borderRadius: 1,
        p: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 20, color: "#057C82" }} />
    </Box>
    <CustomTypography
      color={colors?.textPrimary || "var(--text-heading)"}
      fontSize="15px"
      fontWeight={600}
    >
      {title}
    </CustomTypography>
  </Stack>
);

const PODetailsModal = ({ asn, open, onClose, isAdmin }) => {
  const clientId = localStorage.getItem("client_id");
  const theme = useTheme();
  const { colors } = useDarkMode();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "stock-in complete":
      case "received":
        return { bg: "#E8F5E9", color: "#2E7D32" };
      case "reached warehouse":
      case "picked up":
        return { bg: "#FFF3E0", color: "#E65100" };
      case "supplier confirmed":
      case "inventory ready":
      case "pickup scheduled":
        return { bg: "#FFF3E2", color: "#E65923" };
      case "cancelled":
        return { bg: "#FFEBEE", color: "#C62828" };
      default:
        return { bg: "#E3F2FD", color: "#1565C0" };
    }
  };

  const statusColors = getStatusColor(asn?.status);
  // Computed once: receivingTotals walks every arrival, receipt and line item,
  // so calling it per JSX interpolation would re-walk the whole tree on each
  // render of a modal that already renders those same rows below.
  const receiving = receivingTotals(asn);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primary_color} 60%, ${primary_color} 100%)`,
          px: 3,
          py: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <InventoryOutlinedIcon sx={{ color: "#fff", fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
              Purchase Order Details
            </Typography>
            {asn?.asn_no && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                P.O. No: {asn.asn_no}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          {asn?.status && (
            <Chip
              label={asn.status}
              size="small"
              sx={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                fontWeight: 600,
                fontSize: 12,
              }}
            />
          )}
          <IconButton
            onClick={onClose}
            sx={{
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3, backgroundColor: colors.bgPrimary }}>
        {/* Basic Information */}
        <Box
          sx={{
            backgroundColor: colors.bgPaper,
            borderRadius: 2,
            p: 2.5,
            mb: 2.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            border: `1px solid ${colors.border}`,
          }}
        >
          <SectionHeader
            icon={InfoOutlinedIcon}
            title="Basic Information"
            colors={colors}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label="P.O. No"
                value={asn?.asn_no}
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label="Master P.O."
                value={asn?.purchase_order?.no}
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label="Supplier"
                value={asn?.supplier?.name}
                colors={colors}
              />
            </Grid>
            {!clientId && (
              <Grid item xs={12} sm={6}>
                <ReadOnlyField
                  label="Client"
                  value={asn?.client?.business_name}
                  colors={colors}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* PO Information */}
        <Box
          sx={{
            backgroundColor: colors.bgPaper,
            borderRadius: 2,
            p: 2.5,
            mb: 2.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            border: `1px solid ${colors.border}`,
          }}
        >
          <SectionHeader
            icon={WarehouseOutlinedIcon}
            title="PO Information"
            colors={colors}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label="Bill of Lading Number"
                value={asn?.bol_no}
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyField
                label="Warehouse"
                value={warehouseLabel(asn?.warehouse)}
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyField
                label="PO Date"
                value={formatPoDate(asn, "")}
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyField
                label="Expected Dispatch Date"
                value={
                  asn?.expected_dispatch_date
                    ? formatDate(asn.expected_dispatch_date)
                    : ""
                }
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyField
                label="Confirmed Dispatch Date"
                value={
                  asn?.confirmed_dispatch_date
                    ? formatDate(asn.confirmed_dispatch_date)
                    : ""
                }
                colors={colors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyField
                label="Shipment Date"
                value={
                  asn?.shipment_date
                    ? formatDate(asn.shipment_date)
                    : ""
                }
                colors={colors}
              />
            </Grid>
          </Grid>

          {/* SKU List */}
          {asn?.sku_list?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1.5 }}
              >
                SKU Items ({asn.sku_list.length})
              </Typography>
              <Stack spacing={1.5}>
                {asn.sku_list.map((row, idx) => (
                  <Box
                    key={row._id || idx}
                    sx={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                      backgroundColor: colors.bgSecondary,
                      p: 2,
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: colors.hover,
                        borderColor: colors.border,
                      },
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <ReadOnlyField
                          label="SKU"
                          value={
                            row.sku +
                            (row.variant?.title
                              ? ` (${row.variant.title})`
                              : "") +
                            (row.product?.name ? ` | ${row.product.name}` : "")
                          }
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ReadOnlyField
                          label="Warehouse"
                          value={
                            row.warehouse?.warehouse_id ||
                            row.warehouse?.name ||
                            "N/A"
                          }
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ReadOnlyField
                          label="Qty Requested"
                          value={row.quantity_requested}
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ReadOnlyField
                          label="Qty Confirmed"
                          value={row.quantity_accepted}
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ReadOnlyField
                          label="No of Overpacks"
                          value={row.no_of_overpacks}
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ReadOnlyField
                          label="Overpack Type"
                          value={row.overpack_type}
                          colors={colors}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ReadOnlyField
                          label="Max Items/Overpack"
                          value={row.items_per_overpack}
                          colors={colors}
                        />
                      </Grid>
                      {/* Vendor line detail. Each cell is rendered only when
                          the provider actually sent that field, so an RL_OMS or
                          portal-created line looks exactly as it always has. */}
                      {row.name && row.name !== row.sku && (
                        <Grid item xs={12} sm={6}>
                          <ReadOnlyField
                            label="Vendor Item Name"
                            value={row.name}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {row.price != null && (
                        <Grid item xs={6} sm={3}>
                          <ReadOnlyField
                            label="Unit Price"
                            value={formatMoney(row.price, asn?.costs?.currency)}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {row.quantity_unit && (
                        <Grid item xs={6} sm={3}>
                          <ReadOnlyField
                            label="Unit"
                            value={row.quantity_unit}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {row.vendor_sku && (
                        <Grid item xs={6} sm={3}>
                          <ReadOnlyField
                            label="Vendor SKU"
                            value={row.vendor_sku}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {row.upc && (
                        <Grid item xs={6} sm={3}>
                          <ReadOnlyField
                            label="UPC"
                            value={row.upc}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {/* The join key back to the arrival and receipt lines
                          below — it is what makes "this line, on that
                          delivery" answerable when one PO repeats a SKU. */}
                      {row.unique_id && (
                        <Grid item xs={6} sm={3}>
                          <ReadOnlyField
                            label="Line Ref"
                            value={row.unique_id}
                            colors={colors}
                          />
                        </Grid>
                      )}
                      {asn?.type === "M2O" && row.instructions && (
                        <Grid item xs={12}>
                          <ReadOnlyField
                            label="Instructions"
                            value={row.instructions}
                            colors={colors}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Costs & Shipping Terms */}
        {/*
          Provider-fed and entirely optional: `costs`, `shipping_details` and
          `tags` are only ever populated by a provider that reports them
          (Pipe17 today). A portal-created PO or an RL_OMS one has none of
          this, and the card is hidden rather than rendered empty — the same
          rule the Arrivals section follows below.
        */}
        {hasCommercialDetail(asn) && (
          <Box
            sx={{
              backgroundColor: colors.bgPaper,
              borderRadius: 2,
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <SectionHeader
              icon={PaidOutlinedIcon}
              title="Costs & Shipping Terms"
              colors={colors}
            />
            <Grid container spacing={2}>
              {asn?.costs?.sub_total != null && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Sub Total"
                    value={formatMoney(asn.costs.sub_total, asn.costs.currency)}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.costs?.tax != null && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Tax"
                    value={formatMoney(asn.costs.tax, asn.costs.currency)}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.costs?.total != null && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Total"
                    value={formatMoney(asn.costs.total, asn.costs.currency)}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.costs?.currency && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Currency"
                    value={asn.costs.currency}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.shipping_details?.carrier && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Carrier (requested)"
                    value={asn.shipping_details.carrier}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.shipping_details?.service && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Service"
                    value={asn.shipping_details.service}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.shipping_details?.incoterms && (
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField
                    label="Incoterms"
                    value={asn.shipping_details.incoterms}
                    colors={colors}
                  />
                </Grid>
              )}
              {asn?.shipping_details?.notes && (
                <Grid item xs={12} sm={6}>
                  <ReadOnlyField
                    label="Shipping Notes"
                    value={asn.shipping_details.notes}
                    colors={colors}
                  />
                </Grid>
              )}
              {/* Freight, duty and surcharges — the landed-cost half of the
                  PO's value, which has no single-number equivalent. */}
              {asn?.costs?.additional?.length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors?.textTertiary,
                      fontWeight: 500,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Additional Costs
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                    {asn.costs.additional.map((c, i) => (
                      <Chip
                        key={`${c.name}-${i}`}
                        size="small"
                        label={`${c.name || "Charge"}: ${formatMoney(
                          c.cost,
                          asn.costs.currency,
                        )}${c.quantity ? ` x${c.quantity}` : ""}`}
                        sx={{ fontSize: 12 }}
                      />
                    ))}
                  </Stack>
                </Grid>
              )}
              {asn?.tags?.length > 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors?.textTertiary,
                      fontWeight: 500,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    Tags
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
                    {asn.tags.map((t) => (
                      <Chip key={t} size="small" label={t} sx={{ fontSize: 12 }} />
                    ))}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Exceptions */}
        {/*
          Why the PO is stuck, in the PROVIDER's words. Display-only by design:
          it does not drive status, holds or notifications. An empty array means
          the provider says the PO is clean, so only a non-empty one renders.
        */}
        {asn?.exceptions?.length > 0 && (
          <Box
            sx={{
              backgroundColor: colors.bgPaper,
              borderRadius: 2,
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <SectionHeader
              icon={ReportProblemOutlinedIcon}
              title={`Exceptions (${asn.exceptions.length})`}
              colors={colors}
            />
            <Stack spacing={1.5}>
              {asn.exceptions.map((e, i) => (
                <Box
                  key={e.exception_id || `${e.type}-${i}`}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderLeft: "3px solid #E65100",
                    borderRadius: 1.5,
                    backgroundColor: colors.bgSecondary,
                    p: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ gap: 1, mb: e.details ? 0.5 : 0 }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {e.type || "Exception"}
                    </Typography>
                    {e.severity && (
                      <Chip
                        size="small"
                        label={e.severity}
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    )}
                    {e.priority && (
                      <Chip
                        size="small"
                        label={e.priority}
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    )}
                  </Stack>
                  {e.details && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textTertiary }}
                    >
                      {e.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Lines the catalog could not place */}
        {/*
          A PO line whose SKU is unknown to the core catalog cannot become an
          ASN row (it has no product to point at), so it is dropped — and the
          PO renders visibly short with no explanation. The provider now records
          what it dropped; this is the only place an operator can see it.
        */}
        {asn?.others?.unmatched_lines?.length > 0 && (
          <Box
            sx={{
              backgroundColor: colors.bgPaper,
              borderRadius: 2,
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid #E65100`,
            }}
          >
            <SectionHeader
              icon={ReportProblemOutlinedIcon}
              title={`Not in catalog (${asn.others.unmatched_lines.length})`}
              colors={colors}
            />
            <Typography
              variant="caption"
              sx={{ color: colors.textTertiary, display: "block", mb: 1.5 }}
            >
              These PO lines are not on this PO's SKU list because their SKU is
              not in the product catalog. Import the products and re-sync the
              PO to place them.
            </Typography>
            <Stack spacing={1}>
              {asn.others.unmatched_lines.map((l, i) => (
                <Stack
                  key={`${l.sku}-${i}`}
                  direction="row"
                  flexWrap="wrap"
                  sx={{ gap: 2 }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {l.sku}
                  </Typography>
                  {l.name && l.name !== l.sku && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textTertiary }}
                    >
                      {l.name}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textTertiary }}
                  >
                    Qty: {l.quantity_requested}
                  </Typography>
                  {l.vendor_sku && (
                    <Typography
                      variant="caption"
                      sx={{ color: colors.textTertiary }}
                    >
                      Vendor SKU: {l.vendor_sku}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Arrivals & Receipts */}
        {/*
          Rendered only for POs that actually carry receiving data. The section
          is provider-fed (`asn.arrivals`), so a portal-created PO or one from a
          provider without receiving support has nothing to show, and an empty
          "Arrivals" card would read as "nothing arrived" rather than "this PO
          does not report arrivals".
        */}
        {hasArrivals(asn) && (
          <Box
            sx={{
              backgroundColor: colors.bgPaper,
              borderRadius: 2,
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              sx={{ gap: 1 }}
            >
              <SectionHeader
                icon={MoveToInboxOutlinedIcon}
                title="Arrivals & Receipts"
                colors={colors}
              />
              {/* The one-line answer, so an operator does not have to add up
                  the tables below to see how far along the PO is. */}
              <Chip
                label={`${receiving.received} of ${receiving.ordered} received`}
                size="small"
                sx={{
                  backgroundColor: "#E8F5E9",
                  color: "#2E7D32",
                  fontWeight: 600,
                  fontSize: 12,
                  mb: 2,
                }}
              />
            </Stack>
            <POArrivalsView asn={asn} colors={colors} />
          </Box>
        )}

        {/* Additional Information (Admin only) */}
        {isAdmin && (
          <Box
            sx={{
              backgroundColor: colors.bgPaper,
              borderRadius: 2,
              p: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              border: `1px solid ${colors.border}`,
            }}
          >
            <SectionHeader
              icon={LocalShippingOutlinedIcon}
              title="Additional Information"
              colors={colors}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField
                  label="Driver Name"
                  value={asn?.additional_info?.driver_name}
                  colors={colors}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReadOnlyField
                  label="Vehicle License Plate"
                  value={asn?.additional_info?.vehicle_license}
                  colors={colors}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: colors.bgPaper,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            borderColor: colors.border,
            color: colors.textSecondary,
            "&:hover": {
              borderColor: "#057C82",
              backgroundColor: colors.bgSecondary,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default PODetailsModal;
